/**
 * The message composer.
 *
 * Structure, and why it is shaped this way:
 *
 *     .lf-composer__seeds     one-tap prompts, only once a dataset is staged.
 *                             ABOVE the surface: they are things to ask before
 *                             typing, and below it they made a third stacked
 *                             band under the input.
 *     .lf-composer__surface   the bordered column; owns the focus ring, so the
 *                             attachment tray, the text and the controls light
 *                             up as one control rather than stacked boxes
 *       .lf-composer__tray    the staged dataset, when there is one
 *       .lf-composer__input   the textarea, FULL WIDTH of the surface
 *       .lf-composer__rail    attach · grounding · hint · counter · send/stop
 *
 * Two things this layout is deliberately fixing:
 *
 * - **The textarea must carry `lf-field-bare`.** `theme-overrides.css` styles
 *   every bare `textarea` with a background, border and radius under
 *   `!important`, so a plain `border: none` here loses silently and the field
 *   paints a second bordered box inside the surface — and, on focus, a second
 *   ring inside the surface's own. `lf-field-bare` is that file's supported
 *   opt-out for a field composed into a larger surface.
 *
 * - **The controls sit on a rail UNDER the text, not beside it.** Flanking the
 *   textarea with the attach and send buttons squeezed a long question into a
 *   narrow channel between them; full width is the whole readability win here.
 *
 * Behaviours worth keeping:
 *
 * - **No keyword gate.** The old input ran the draft through a hardcoded
 *   allow/block list and popped a modal on a miss, so "can I count people in a
 *   queue?" was refused before it ever left the browser. Scope is the model's
 *   job; the only client-side limit is length.
 *
 * - **The textarea grows with the text** up to a ceiling, then scrolls.
 *
 * - **Stop replaces Send while streaming,** in the same spot.
 *
 * - **An attachment alone is a valid message.** `canSend` used to require two
 *   typed characters, and the hook's `send` returned early on empty text — both
 *   had to be relaxed together, because relaxing one leaves the paperclip
 *   apparently doing nothing.
 *
 * - **There are TWO pickers, and the folder one needs `webkitdirectory` set
 *   imperatively.** The user's own phrasing was "zip ya koi folder" — a folder
 *   is at least as natural as zipping it first, and a `.zip`-only `accept`
 *   greys every folder out in the OS dialog, so the feature simply was not
 *   reachable from the paperclip. `webkitdirectory` is non-standard and React
 *   does not type it, so it is applied to the node in an effect rather than as
 *   a JSX prop.
 *
 * - **Both pickers hand over `event.target.files`, the whole FileList.**
 *   Passing `files[0]` is what broke this before: a folder minus its contents
 *   is not a dataset, and the hook's list-normaliser is what makes a single
 *   file safe to pass alongside it.
 */
import React, { useCallback, useEffect, useRef } from 'react';
import { useI18n } from '../../../i18n';
import ComposerAttachTray from './ComposerAttachTray';

/** Matches MAX_MESSAGE_CHARS in i_ai_assistant.py — keep the two in step. */
const MAX_CHARS = 6000;
/**
 * Matches MIN_MESSAGE_CHARS in i_ai_assistant.py.
 *
 * Enforced here purely so a one-character send cannot round-trip into a 400 and
 * an error banner. It is not a scope gate — the old keyword filter was, and it
 * rejected real questions.
 */
const MIN_CHARS = 2;
/** Show the counter only when the limit is close enough to matter. */
const COUNTER_THRESHOLD = 4800;
const MAX_TEXTAREA_PX = 200;

const AssistantComposer = ({
  value,
  onChange,
  onSubmit,
  onStop,
  onEditLast,
  isStreaming,
  includeContext,
  onToggleContext,
  contextAvailable,
  onOpenShortcuts,
  attachment,
  onAttachFile,
  onRemoveAttachment,
  attachDisabled,
  seeds,
}) => {
  const { t } = useI18n();
  const inputRef = useRef(null);
  const fileRef = useRef(null);
  const folderRef = useRef(null);

  // `webkitdirectory` / `directory` are non-standard attributes; setting them
  // on the DOM node avoids React dropping or warning about unknown props.
  useEffect(() => {
    const node = folderRef.current;
    if (!node) return;
    node.setAttribute('webkitdirectory', '');
    node.setAttribute('directory', '');
  }, []);

  /** Grow to fit the content, then scroll. */
  const resize = useCallback(() => {
    const node = inputRef.current;
    if (!node) return;
    node.style.height = 'auto';
    node.style.height = `${Math.min(node.scrollHeight, MAX_TEXTAREA_PX)}px`;
  }, []);

  useEffect(resize, [value, resize]);

  // Return focus once a reply finishes so the next question needs no click.
  useEffect(() => {
    if (!isStreaming) inputRef.current?.focus();
  }, [isStreaming]);

  const ready = !!attachment && attachment.status === 'ready';
  const busyReading = !!attachment && attachment.status === 'reading';

  const submit = useCallback(
    (event) => {
      event?.preventDefault();
      const text = (value || '').trim();
      if (isStreaming || busyReading) return;
      if (text.length > MAX_CHARS) return;
      // An attachment is a message on its own; otherwise text is required.
      if (!ready && text.length < MIN_CHARS) return;
      onSubmit?.(text);
    },
    [busyReading, isStreaming, onSubmit, ready, value],
  );

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent?.isComposing) {
      // isComposing guards Korean/Japanese IME: Enter commits the candidate
      // word there, and sending on it truncates what the user was typing.
      event.preventDefault();
      submit(event);
      return;
    }
    if (event.key === 'ArrowUp' && !(value || '').length) {
      // Empty box + Up = edit the previous question, as in a shell.
      event.preventDefault();
      onEditLast?.();
    }
  };

  const pickFile = useCallback(() => fileRef.current?.click(), []);
  const pickFolder = useCallback(() => folderRef.current?.click(), []);

  const handleFile = useCallback(
    (event) => {
      // The WHOLE FileList: a folder pick is many files, and dropping all but
      // the first is how the folder path died last time.
      const files = Array.from(event.target.files || []);
      // Reset first: without this, picking the SAME file twice fires no change
      // event, so a failed read could never be retried from the picker.
      event.target.value = '';
      if (files.length) onAttachFile?.(files);
    },
    [onAttachFile],
  );

  const length = (value || '').length;
  const overLimit = length > MAX_CHARS;
  const showCounter = length >= COUNTER_THRESHOLD || overLimit;
  // Trimmed length is the one that matters: "  a  " is 5 characters and one word.
  const canSend =
    !overLimit && !busyReading && (ready || (value || '').trim().length >= MIN_CHARS);

  return (
    <form className="lf-composer" onSubmit={submit}>
      {ready && !!(seeds || []).length && (
        <div className="lf-composer__seeds">
          {(seeds || []).map((seed) => (
            <button
              key={seed.id}
              type="button"
              className="lf-composer__seed"
              onClick={() => onChange?.(seed.prompt)}
            >
              <i className={`fa ${seed.icon}`} aria-hidden="true" />
              {seed.label}
            </button>
          ))}
        </div>
      )}

      <div
        className={`lf-composer__surface${overLimit ? ' lf-composer__surface--invalid' : ''}`}
      >
        {attachment && (
          <div className="lf-composer__tray">
            <ComposerAttachTray
              attachment={attachment}
              onRemove={onRemoveAttachment}
              onReplace={pickFile}
              disabled={isStreaming}
            />
          </div>
        )}

        <input
          ref={fileRef}
          type="file"
          className="lf-composer__file"
          accept=".zip,application/zip,application/x-zip-compressed"
          onChange={handleFile}
          tabIndex={-1}
          aria-hidden="true"
        />

        <input
          ref={folderRef}
          type="file"
          className="lf-composer__file"
          multiple
          onChange={handleFile}
          tabIndex={-1}
          aria-hidden="true"
        />

        <textarea
          ref={inputRef}
          className="lf-composer__input lf-field-bare"
          value={value}
          onChange={(event) => onChange?.(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={
            ready ? t('assistant.attach.hint') : t('assistant.composer.placeholder')
          }
          rows={1}
          aria-label={t('assistant.composer.label')}
          aria-invalid={overLimit ? 'true' : 'false'}
        />

        <div className="lf-composer__rail">
          <button
            type="button"
            className={`lf-composer__attach${attachment ? ' is-on' : ''}`}
            onClick={pickFile}
            disabled={attachDisabled || isStreaming}
            title={t('assistant.attach.button')}
            aria-label={t('assistant.attach.button')}
          >
            <i className="fa fa-paperclip" aria-hidden="true" />
          </button>

          <button
            type="button"
            className="lf-composer__attach"
            onClick={pickFolder}
            disabled={attachDisabled || isStreaming}
            title={t('assistant.attach.folderButton')}
            aria-label={t('assistant.attach.folderButton')}
          >
            <i className="fa fa-folder-open" aria-hidden="true" />
          </button>

          <button
            type="button"
            className={`lf-composer__chip${includeContext ? ' lf-composer__chip--on' : ''}`}
            onClick={onToggleContext}
            disabled={!contextAvailable}
            aria-pressed={includeContext}
            title={
              contextAvailable
                ? t('assistant.composer.contextHint')
                : t('assistant.composer.contextUnavailable')
            }
          >
            <i
              className={`fa ${includeContext ? 'fa-database' : 'fa-eye-slash'}`}
              aria-hidden="true"
            />
            <span className="lf-composer__chip-text">
              {includeContext
                ? t('assistant.composer.contextOn')
                : t('assistant.composer.contextOff')}
            </span>
          </button>

          <span className="lf-composer__spacer" />

          {/*
            The hint and the counter share this slot and never both appear: side
            by side they read as one run-on string ("…for a new line 6,204 /
            6,000"), and once the draft is near the limit the count is the only
            one of the two still worth the space.
          */}
          {showCounter ? (
            <span
              className={`lf-composer__count${overLimit ? ' lf-composer__count--over' : ''}`}
              role={overLimit ? 'alert' : undefined}
            >
              {length.toLocaleString()} / {MAX_CHARS.toLocaleString()}
            </span>
          ) : (
            <span className="lf-composer__hint lf-hide-md">{t('assistant.composer.hint')}</span>
          )}

          <button
            type="button"
            className="lf-composer__shortcuts lf-hide-sm"
            onClick={onOpenShortcuts}
            title={t('assistant.shortcuts.title')}
            aria-label={t('assistant.shortcuts.title')}
          >
            <i className="fa fa-keyboard" aria-hidden="true" />
          </button>

          {isStreaming ? (
            <button
              type="button"
              className="lf-composer__stop"
              onClick={onStop}
              title={t('assistant.composer.stop')}
              aria-label={t('assistant.composer.stop')}
            >
              <i className="fa fa-stop" aria-hidden="true" />
            </button>
          ) : (
            <button
              type="submit"
              className="lf-composer__send"
              disabled={!canSend}
              title={t('assistant.composer.send')}
              aria-label={t('assistant.composer.send')}
            >
              <i className="fa fa-paper-plane" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
    </form>
  );
};

export default AssistantComposer;
